import React from 'react';
import { motion, AnimatePresence, Variants, LayoutGroup } from 'framer-motion';

// =============================================================================
// STAGGERED LIST CONTAINER
// =============================================================================

interface StaggeredListProps {
  children: React.ReactNode;
  staggerDelay?: number;
  className?: string;
  style?: React.CSSProperties;
  direction?: 'up' | 'down' | 'left' | 'right';
}

export const StaggeredList: React.FC<StaggeredListProps> = ({
  children,
  staggerDelay = 0.05,
  className,
  style,
  direction = 'up',
}) => {
  const customVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: 0.1,
      },
    },
  };

  return (
    <motion.div
      className={className}
      style={style}
      variants={customVariants}
      initial="hidden"
      animate="visible"
    >
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;

        return (
          <StaggeredItem key={index} direction={direction}>
            {child}
          </StaggeredItem>
        );
      })}
    </motion.div>
  );
};

// =============================================================================
// STAGGERED ITEM
// =============================================================================

interface StaggeredItemProps {
  children: React.ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right';
  className?: string;
}

const getDirectionOffset = (direction: 'up' | 'down' | 'left' | 'right') => {
  switch (direction) {
    case 'up':
      return { y: 20 };
    case 'down':
      return { y: -20 };
    case 'left':
      return { x: 20 };
    case 'right':
      return { x: -20 };
    default:
      return { y: 20 };
  }
};

const StaggeredItem: React.FC<StaggeredItemProps> = ({
  children,
  direction = 'up',
  className,
}) => {
  const offset = getDirectionOffset(direction);

  const itemVariants: Variants = {
    hidden: {
      opacity: 0,
      ...offset,
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
  };

  return (
    <motion.div variants={itemVariants} className={className}>
      {children}
    </motion.div>
  );
};

// =============================================================================
// ANIMATED LIST WITH REORDERING
// =============================================================================

interface AnimatedReorderListProps<T> {
  items: T[];
  keyExtractor: (item: T) => string;
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
  itemClassName?: string;
  style?: React.CSSProperties;
}

export function AnimatedReorderList<T>({
  items,
  keyExtractor,
  renderItem,
  className,
  itemClassName,
  style,
}: AnimatedReorderListProps<T>) {
  return (
    <LayoutGroup>
      <motion.div className={className} style={style}>
        <AnimatePresence mode="popLayout">
          {items.map((item, index) => (
            <motion.div
              key={keyExtractor(item)}
              layout
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{
                layout: { type: 'spring', stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
                scale: { duration: 0.2 },
              }}
              className={itemClassName}
            >
              {renderItem(item, index)}
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </LayoutGroup>
  );
}

// =============================================================================
// CASCADING GRID
// =============================================================================

interface CascadingGridProps {
  children: React.ReactNode;
  columns?: number;
  gap?: number;
  className?: string;
  style?: React.CSSProperties;
}

export const CascadingGrid: React.FC<CascadingGridProps> = ({
  children,
  columns = 3,
  gap = 16,
  className,
  style,
}) => {
  const gridVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 260,
        damping: 20,
      },
    },
  };

  return (
    <motion.div
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap,
        ...style,
      }}
      variants={gridVariants}
      initial="hidden"
      animate="visible"
    >
      {React.Children.map(children, (child, index) => (
        <motion.div key={index} variants={itemVariants}>
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
};

// =============================================================================
// ACCORDION LIST (Expandable items)
// =============================================================================

interface AccordionItem {
  id: string;
  title: React.ReactNode;
  content: React.ReactNode;
}

interface AnimatedAccordionProps {
  items: AccordionItem[];
  allowMultiple?: boolean;
  className?: string;
  itemClassName?: string;
}

export const AnimatedAccordion: React.FC<AnimatedAccordionProps> = ({
  items,
  allowMultiple = false,
  className,
  itemClassName,
}) => {
  const [expandedIds, setExpandedIds] = React.useState<string[]>([]);

  const toggleItem = (id: string) => {
    if (allowMultiple) {
      setExpandedIds((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
      );
    } else {
      setExpandedIds((prev) => (prev.includes(id) ? [] : [id]));
    }
  };

  return (
    <div className={className}>
      {items.map((item) => {
        const isExpanded = expandedIds.includes(item.id);

        return (
          <motion.div
            key={item.id}
            className={itemClassName}
            initial={false}
            animate={{
              backgroundColor: isExpanded
                ? 'rgba(var(--accent-rgb), 0.05)'
                : 'transparent',
            }}
            style={{
              borderRadius: 8,
              overflow: 'hidden',
              marginBottom: 8,
            }}
          >
            {/* Header */}
            <motion.button
              onClick={() => toggleItem(item.id)}
              style={{
                width: '100%',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit',
                fontSize: 'inherit',
              }}
              whileHover={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
              whileTap={{ scale: 0.99 }}
            >
              {item.title}
              <motion.svg
                width={20}
                height={20}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              >
                <polyline points="6 9 12 15 18 9" />
              </motion.svg>
            </motion.button>

            {/* Content */}
            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { type: 'spring', stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 },
                  }}
                  style={{ overflow: 'hidden' }}
                >
                  <div style={{ padding: '0 16px 16px' }}>{item.content}</div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

// =============================================================================
// SLIDE IN LIST (Page transitions)
// =============================================================================

interface SlideInListProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  delay?: number;
}

export const SlideInList: React.FC<SlideInListProps> = ({
  children,
  className,
  style,
  delay = 0,
}) => {
  return (
    <motion.div
      className={className}
      style={style}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.1,
            delayChildren: delay,
          },
        },
      }}
    >
      {React.Children.map(children, (child, index) => (
        <motion.div
          key={index}
          variants={{
            hidden: { opacity: 0, x: -30 },
            visible: {
              opacity: 1,
              x: 0,
              transition: {
                type: 'spring',
                stiffness: 100,
                damping: 15,
              },
            },
          }}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
};

// =============================================================================
// FADE STAGGER (Simple fade in)
// =============================================================================

interface FadeStaggerProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  staggerDelay?: number;
}

export const FadeStagger: React.FC<FadeStaggerProps> = ({
  children,
  className,
  style,
  staggerDelay = 0.1,
}) => {
  return (
    <motion.div
      className={className}
      style={style}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: staggerDelay,
          },
        },
      }}
    >
      {React.Children.map(children, (child, index) => (
        <motion.div
          key={index}
          variants={{
            hidden: { opacity: 0, y: 10 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.4, ease: 'easeOut' },
            },
          }}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
};

export default {
  StaggeredList,
  AnimatedReorderList,
  CascadingGrid,
  AnimatedAccordion,
  SlideInList,
  FadeStagger,
};
